import { mockStore } from '../../mock/store';
import { tenantApi } from './tenantApi';
import { memberApi } from './memberApi';
import type { Tenant, CreateTenantInput, AddTenantUserInput } from '../../types/tenant';
import type { User } from '../../types/user';
import type { SchemaProfile } from '../../types/schemaProfile';
import {
  getSchemaProfileFromGit,
  listSchemaProfilesFromGit,
  reloadSchemaProfile,
  type SchemaGitConnection,
} from '../schemaProfileApi';
import {
  applyWorkflowProfile,
  listWorkflowProfilesFromGit,
} from '../workflowProfileApi';
import type { AdminScope } from '../../types/adminScope';
import type { CreateTenantServiceAccountInput } from './tenantApi';

export const tenantService = {
  listForScope: (scope: AdminScope): Promise<Tenant[]> =>
    scope.isPlatformAdmin ? tenantApi.list() : tenantApi.listManaged(),

  list: (): Promise<Tenant[]> => tenantApi.list(),
  listManaged: (): Promise<Tenant[]> => tenantApi.listManaged(),
  get: (id: string): Promise<Tenant | undefined> => tenantApi.get(id),
  create: (input: CreateTenantInput): Promise<Tenant> => tenantApi.create(input),
  getRoster: (tenantId: string) => memberApi.getRoster(tenantId),
  addUser: (input: AddTenantUserInput) =>
    memberApi.addUser(input.tenantId, {
      email: input.email,
      name: input.name,
      username: input.username,
      role: input.role,
      createInKeycloak: input.createInKeycloak,
      password: input.password,
    }),
  removeAdmin: (tenantId: string, userId: string) => memberApi.removeAdmin(tenantId, userId),
  removeMember: (tenantId: string, userId: string) => memberApi.removeMember(tenantId, userId),
  listServiceAccounts: (tenantId: string) => tenantApi.listServiceAccounts(tenantId),
  createServiceAccount: (tenantId: string, input: CreateTenantServiceAccountInput) =>
    tenantApi.createServiceAccount(tenantId, input),
  getSchemaProfile: async (tenantId: string) => {
    const tenant = await tenantApi.get(tenantId);
    if (!tenant?.schemaProfile) {
      return undefined;
    }
    return {
      tenantId,
      schemaProfile: tenant.schemaProfile,
      createdAt: tenant.createdAt,
      updatedAt: tenant.updatedAt,
    };
  },
  setSchemaProfile: async (tenantId: string, schemaProfile: string) => {
    const tenant = await tenantApi.setSchemaProfile(tenantId, schemaProfile);
    return {
      tenantId,
      schemaProfile: tenant.schemaProfile!,
      createdAt: tenant.createdAt,
      updatedAt: tenant.updatedAt,
    };
  },
  clearSchemaProfile: (tenantId: string): Promise<void> => mockStore.clearTenantSchemaProfile(tenantId),
};

export const userService = {
  list: (): Promise<User[]> => mockStore.listUsers(),
  get: (id: string): Promise<User | undefined> => mockStore.getUser(id),
};

export const schemaProfileService = {
  list: (conn: SchemaGitConnection) => listSchemaProfilesFromGit(conn),
  get: (id: string, conn: SchemaGitConnection): Promise<SchemaProfile | undefined> =>
    getSchemaProfileFromGit(id, conn),
  reload: reloadSchemaProfile,
};

export const workflowProfileService = {
  list: (conn: SchemaGitConnection) => listWorkflowProfilesFromGit(conn),
  apply: applyWorkflowProfile,
};

export { adminScopeApi } from './adminScopeApi';
export {
  fetchSchemaIndexStatus,
  ensureAllSchemaIndexes,
  ensureSchemaIndexes,
  listSchemaIndexProfiles,
} from '../schemaIndexApi';
